import React, { useState, useEffect } from 'react';
import {
  Plus,
  Filter,
  Search,
  List,
  Grid,
  Mail,
  Archive,
  Trash2,
  Users,
  Download,
  Upload,
  ChevronDown,
} from 'lucide-react';
import EmptyState from '../components/EmptyState';
import ResourceModal from '../components/ResourceModal';
import ResourceDetailsModal from '../components/ResourceDetailsModal';
import { getResources, Resource } from '../lib/resources';
import { useBusiness } from '../contexts/BusinessContext';
import PermissionGuard from '../components/PermissionGuard';

const statusOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'All', value: 'all' },
];

export default function Resources() {
  const { selectedBusiness, role } = useBusiness();
  const [viewType, setViewType] = useState('table');
  const [selectedResources, setSelectedResources] = useState(new Set());
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]); // Default to 'Active'
  const [searchQuery, setSearchQuery] = useState('');
  const [isNewResourceModalOpen, setIsNewResourceModalOpen] = useState(false);
  const [isResourceDetailsModalOpen, setIsResourceDetailsModalOpen] =
    useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(
    null
  );
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadResources = async () => {
    if (!selectedBusiness?.id) {
      console.log('No business selected, skipping resource fetch');
      setLoading(false);
      return;
    }

    try {
      console.log('Loading resources for business:', selectedBusiness.id);
      setLoading(true);
      const data = await getResources(selectedBusiness.id);
      console.log('Resources loaded:', data);
      setResources(data);

      // Update selected resource if it exists
      if (selectedResource) {
        const updatedResource = data.find((r) => r.id === selectedResource.id);
        if (updatedResource) {
          setSelectedResource(updatedResource);
        }
      }

      setError(null);
    } catch (err) {
      console.error('Error loading resources:', err);
      setError(err instanceof Error ? err.message : 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log(
      'Resources component mounted, selectedBusiness:',
      selectedBusiness
    );
    loadResources();
  }, [selectedBusiness?.id]);

  const filteredResources = resources.filter((resource) => {
    // Apply status filter
    if (
      selectedStatus.value !== 'all' &&
      resource.status !== selectedStatus.value
    ) {
      return false;
    }

    const searchLower = searchQuery.toLowerCase();
    return (
      resource.first_name.toLowerCase().includes(searchLower) ||
      resource.last_name.toLowerCase().includes(searchLower) ||
      resource.email.toLowerCase().includes(searchLower) ||
      resource.role.toLowerCase().includes(searchLower)
    );
  });

  const toggleAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedResources(new Set(resources.map((_, index) => index)));
    } else {
      setSelectedResources(new Set());
    }
  };

  const toggleResource = (index: number) => {
    const newSelected = new Set(selectedResources);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedResources(newSelected);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'inactive':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const handleResourceClick = (resource: Resource) => {
    setSelectedResource(resource);
    setIsResourceDetailsModalOpen(true);
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-10rem)]">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Resources
          </h2>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard permission="resources.pageAccess">
      <div className="flex flex-col gap-6 pb-24 md:pb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 mb-6">
          <h1 className="text-xl font-semibold text-gray-900">Resources</h1>
          {role === 'admin' && (
            <button
              onClick={() => setIsNewResourceModalOpen(true)}
              className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 transition-colors"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add Team Member
            </button>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-[#e1e3e5]">
          <div className="px-4 py-3 border-b border-[#e1e3e5] flex flex-col sm:flex-row gap-3 sm:gap-0 justify-between items-stretch sm:items-center">
            <div className="relative flex-1">
              <button
                onClick={() => setIsStatusOpen(!isStatusOpen)}
                className="w-full sm:w-auto inline-flex items-center text-sm font-medium text-gray-900 hover:text-gray-700 focus:outline-none"
              >
                Status: {selectedStatus.label}
                <ChevronDown
                  className={`ml-1.5 h-4 w-4 transition-transform duration-200 ${
                    isStatusOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isStatusOpen && (
                <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                  <div className="py-1" role="menu" aria-orientation="vertical">
                    {statusOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSelectedStatus(option);
                          setIsStatusOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm ${
                          selectedStatus.value === option.value
                            ? 'bg-gray-100 text-gray-900'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        role="menuitem"
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <div className="relative flex-1 sm:flex-initial">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search team members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full sm:w-64 pl-9 pr-4 py-1.5 text-xs bg-gray-50 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <button
                onClick={() => setIsStatusOpen(!isStatusOpen)}
                className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100"
              >
                <Filter className="h-3.5 w-3.5 mr-1.5" />
                Filter
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                <p className="mt-4 text-sm text-gray-600">Loading resources...</p>
              </div>
            </div>
          ) : resources.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <EmptyState
                icon={<Users className="w-8 h-8 text-gray-400" />}
                title="No team members yet"
                description="Start adding team members to manage your workforce effectively."
                actionLabel="Add your first team member"
                onAction={() => setIsNewResourceModalOpen(true)}
              />
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <table className="hidden sm:table min-w-full divide-y divide-[#e1e3e5]">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          className="h-3 w-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          checked={selectedResources.size === resources.length}
                          onChange={toggleAll}
                        />
                      </div>
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Team Member
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Permission Role
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Active
                    </th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#e1e3e5]">
                  {filteredResources.map((resource, index) => (
                    <tr
                      key={resource.id}
                      className="hover:bg-[#f6f6f7] transition-colors cursor-pointer"
                      onClick={() => handleResourceClick(resource)}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          className="h-3 w-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          checked={selectedResources.has(index)}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleResource(index);
                          }}
                        />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-8 w-8 rounded-full bg-black flex items-center justify-center text-xs font-medium text-white overflow-hidden">
                            {resource.avatar_url ? (
                              <img 
                                src={resource.avatar_url} 
                                alt={`${resource.first_name} ${resource.last_name}`}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              `${resource.first_name[0]}${resource.last_name[0]}`
                            )}
                          </div>
                          <div className="ml-3">
                            <div className="text-xs font-medium text-gray-900">
                              {`${resource.first_name} ${resource.last_name}`}
                            </div>
                            <div className="text-xs text-gray-500">
                              {resource.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                        {resource.permission_role?.name || (
                          <span className="text-gray-400 italic">No permissions assigned</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 inline-flex text-xs leading-5 font-medium rounded-full ${getStatusColor(
                            resource.status
                          )}`}
                        >
                          {resource.status.charAt(0).toUpperCase() +
                            resource.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                        {resource.last_active
                          ? new Date(resource.last_active).toLocaleDateString()
                          : 'No activity yet'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {/* Action buttons removed */}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Mobile List View */}
              <div className="sm:hidden divide-y divide-gray-200">
                {filteredResources.map((resource) => (
                  <div
                    key={resource.id}
                    className="p-4 hover:bg-gray-50"
                    onClick={() => handleResourceClick(resource)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className="flex items-center h-full">
                          <input
                            type="checkbox"
                            className="h-3 w-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            checked={selectedResources.has(filteredResources.findIndex(r => r.id === resource.id))}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleResource(filteredResources.findIndex(r => r.id === resource.id));
                            }}
                          />
                        </div>
                        <div>
                          <div className="flex items-center">
                            <div className="h-8 w-8 rounded-full bg-black flex items-center justify-center text-xs font-medium text-white overflow-hidden">
                              {resource.avatar_url ? (
                                <img 
                                  src={resource.avatar_url} 
                                  alt={`${resource.first_name} ${resource.last_name}`}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                `${resource.first_name[0]}${resource.last_name[0]}`
                              )}
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                {resource.first_name} {resource.last_name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {resource.email}
                              </div>
                            </div>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <span className="text-xs text-gray-500">
                              Permissions: {resource.permission_role?.name || 'None'}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                              getStatusColor(resource.status)
                            }`}>
                              {resource.status.charAt(0).toUpperCase() + resource.status.slice(1)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <ResourceModal
          isOpen={isNewResourceModalOpen}
          onClose={() => setIsNewResourceModalOpen(false)}
          onSuccess={() => {
            setIsNewResourceModalOpen(false);
            loadResources();
          }}
          businessId={selectedBusiness?.id}
        />

        <ResourceDetailsModal
          isOpen={isResourceDetailsModalOpen}
          onClose={() => {
            setIsResourceDetailsModalOpen(false);
            setSelectedResource(null);
          }}
          resource={selectedResource}
          onUpdate={loadResources}
        />
      </div>
    </PermissionGuard>
  );
}